import styled from '@emotion/styled'
import { isPubstackUserRole } from '@pubstack/common/src/rolesAndFeatures'
import { Site } from '@pubstack/common/src/site'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useSites, useUpdateSite } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { Link } from '~/components/Link'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import Table, { EmptyTable, handleTableSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { SettingsSitesAddSiteFlyout } from '~/modules/settings/sites/SettingsSitesAddSiteFlyout'
import { SettingsSitesEditSiteFlyout } from '~/modules/settings/sites/SettingsSitesEditSiteFlyout'
import { SUPPORT_MAIL } from '~/utils/constants'
import { SETTINGS_NAV_CONFIG } from '~/utils/settings'
import { SettingsSitesArchiveSiteModal } from './SettingsSitesArchiveSiteModal'

const SettingsSitesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const SettingsSitesHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const ActionsTableCell = styled(TableCell)`
  width: 0px; /** necessary to get the last cell to shrink down to hug action buttons */
`

const SettingsSitesActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const UnarchiveButton: FC<{ site: Site }> = memo(({ site }) => {
  const user = useAuthenticatedUser()
  const { mutateAsync: unArchiveSite } = useUpdateSite(user.currentScopeId, site.id)
  return (
    <Tooltip title={'Unarchive'}>
      <Button
        onClick={(event) => {
          event.stopPropagation()
          unArchiveSite({ ...site, enabled: true })
        }}
        variant={'tertiary'}
        iconName={'unarchive'}
        trackingLabel={'common-settings-sites-unarchive'}
      />
    </Tooltip>
  )
})

export const SettingsSitesPage = () => {
  const user = useAuthenticatedUser()
  const toast = useToast()
  const [lastSearch, setLastSearch] = useState('')
  const { data: sites, isLoading, isFetching, isError } = useSites(user.currentScopeId, lastSearch)

  const currentRole = user.getScopeRole() ?? ''
  const canArchiveSites = !['user', 'demo'].includes(currentRole)
  // Narrower than archiving: unarchiving restores the site's stacks and bid params mappings, so the API
  // now refuses that transition to anyone but Pubstack. Hiding the button keeps this page from offering
  // an owner a click that can only end in a 403.
  const canUnarchiveSites = isPubstackUserRole(user.userRoles)
  const canEditSites = !['demo'].includes(currentRole)

  const onCopyToClipBoard = (label: string) => {
    toast.success(
      <>
        <strong>{label}</strong> was successfully copied to your clipboard.
      </>
    )
  }

  const hasActions = canEditSites || canArchiveSites // Note: can lead to rendering of Action column if all sites are archived and user doesn't have the right to unarchive
  const modal = useGlobalModal()
  const sitesCols: TableColumns<Omit<Site, 'enabled' | 'adManagementEnabled'>> = [
    { name: 'Site', attributeSort: 'name', isSortable: true, order: 'ascending' },
    { name: 'Site ID', attributeSort: 'id', isSortable: true, order: 'none' },
    ...(hasActions ? ([{ name: 'Action', isSortable: false }] as TableColumns) : []),
  ]

  const [columns, setColumns] = useState<TableColumns<Site>>(sitesCols)

  const { control } = useForm({
    defaultValues: {
      search: '',
    },
  })

  const search = useWatch({ control, name: 'search' })
  useEffect(() => {
    if (lastSearch !== search) {
      const timeout = setTimeout(() => {
        setLastSearch(search)
      }, 400)

      return () => clearTimeout(timeout)
    }
  }, [search])

  const sortSites = useMemo(() => {
    return (sites: Site[]) => handleTableSort(columns, sites)
  }, [columns])

  const enabledSites = useMemo(() => sites.filter((s) => s.enabled), [sites])
  const disabledSites = useMemo(() => sites.filter((s) => !s.enabled), [sites])
  const sortedSites = useMemo(() => [...sortSites(enabledSites), ...sortSites(disabledSites)], [enabledSites, disabledSites, sortSites])
  const flyout = useGlobalFlyout()

  const validateSiteName = (site: string | boolean | Omit<Site, 'id'> | Omit<Site, 'id'>[] | undefined) => {
    if (typeof site === 'string') {
      return !sites.some((s) => s.name.toLocaleLowerCase().trim() === site?.toLocaleLowerCase().trim())
    }
    return false
  }

  return (
    <SettingsSitesWrapper>
      <SettingsSitesHeader>
        <Input control={control} name={'search'} label={'Search'} labelIsPlaceholder iconLeft={'search'} />
        {canEditSites && (
          <Button
            iconName={'add_site'}
            onClick={() =>
              flyout.open(
                SettingsSitesAddSiteFlyout,
                { color: SETTINGS_NAV_CONFIG.sites.color, closeTrackingLabel: 'common-settings-sites-creation-close' },
                {
                  validateSite: validateSiteName,
                  onAdd: () => {
                    flyout.close()
                  },
                }
              )
            }
            trackingLabel={'common-settings-sites-new'}
          >
            Add site
          </Button>
        )}
      </SettingsSitesHeader>
      <Table isLoading={isLoading || isFetching} columns={columns} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {sortedSites.length ? (
          sortedSites.map((site, index) => {
            const isArchived = !site.enabled
            return (
              <TableRow
                clickable={
                  canEditSites && !isArchived
                    ? {
                        onClick: () => {
                          flyout.open(
                            SettingsSitesEditSiteFlyout,
                            { color: SETTINGS_NAV_CONFIG.sites.color, closeTrackingLabel: 'common-settings-sites-edit-close' },
                            {
                              site,
                              onCopyToClipBoard,
                            }
                          )
                        },
                        trackingLabel: 'common-settings-sites-edit',
                      }
                    : undefined
                }
                archived={isArchived}
                key={index}
              >
                <TableCell>{site.name}</TableCell>
                <TableCell>{site.id}</TableCell>
                {hasActions && (
                  <ActionsTableCell>
                    <SettingsSitesActions>
                      {canEditSites && !isArchived && (
                        <Tooltip title={'Edit'}>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation()
                              flyout.open(
                                SettingsSitesEditSiteFlyout,
                                { color: SETTINGS_NAV_CONFIG.sites.color, closeTrackingLabel: 'common-settings-sites-edit-close' },
                                {
                                  site,
                                  onCopyToClipBoard,
                                }
                              )
                            }}
                            variant={'tertiary'}
                            trackingLabel={'common-settings-sites-icon-edit'}
                            iconName={'edit'}
                          />
                        </Tooltip>
                      )}
                      {canArchiveSites &&
                        (!isArchived ? (
                          <Tooltip title={'Archive'}>
                            <Button
                              onClick={(event) => {
                                event.stopPropagation()
                                modal.open(SettingsSitesArchiveSiteModal, { site })
                              }}
                              variant={'tertiary'}
                              iconName={'archive'}
                              trackingLabel={'common-settings-sites-archive'}
                            />
                          </Tooltip>
                        ) : (
                          canUnarchiveSites && <UnarchiveButton site={site} />
                        ))}
                    </SettingsSitesActions>
                  </ActionsTableCell>
                )}
              </TableRow>
            )
          })
        ) : (
          <TableRow>
            <TableCell colspan={columns.length}>
              <EmptyTable>
                {isError ? (
                  <div>
                    <h2>An error occurred when trying to display your sites</h2>
                    <p>
                      Please refresh the page or contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> if the issue persists.
                    </p>
                  </div>
                ) : (
                  <div>
                    <h2>No sites</h2>
                    <p>You have no sites set up.</p>
                    {canEditSites && (
                      <Button
                        iconName={'add_site'}
                        onClick={() =>
                          flyout.open(
                            SettingsSitesAddSiteFlyout,
                            { color: SETTINGS_NAV_CONFIG.sites.color, closeTrackingLabel: 'common-settings-sites-creation-close' },
                            {
                              validateSite: validateSiteName,
                            }
                          )
                        }
                        trackingLabel={'common-settings-sites-new'}
                      >
                        Add site
                      </Button>
                    )}
                  </div>
                )}
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </SettingsSitesWrapper>
  )
}
